/**
 * Sport Configuration
 *
 * Central configuration for all sports with their distances, time ranges,
 * workout types, and training parameters.
 */

import { Sport, RaceType, WorkoutType } from 'types/app';
import { ReactNode } from 'react';

export interface DistanceOption {
  id: string;
  label: string;
  desc: string;
  icon?: ReactNode;
  raceType: RaceType;
}

export interface GoalTimeLevel {
  level: string;
  time: string;
  value: string;
}

export interface SportConfig {
  name: string;
  sport: Sport;
  distances: DistanceOption[];
  goalTimes: Record<string, GoalTimeLevel[]>;
  workoutTypes: WorkoutType[];
  defaultTrainingDays: number;
  planPath: string;
  units: 'km' | 'mi' | 'm';
  description: string;
}

// Running Configuration
export const runningConfig: SportConfig = {
  name: 'Running',
  sport: 'running',
  distances: [
    { id: '5k', label: '5K', desc: '3.1 miles', raceType: '5K' },
    { id: '10k', label: '10K', desc: '6.2 miles', raceType: '10K' },
    {
      id: 'half',
      label: 'Half Marathon',
      desc: '13.1 miles / 21.1 km',
      raceType: 'Half Marathon',
    },
    {
      id: 'full',
      label: 'Marathon',
      desc: '26.2 miles / 42.2 km',
      raceType: 'Marathon',
    },
  ],
  goalTimes: {
    '5k': [
      { level: 'Beginner', time: '30–35 min', value: 'beginner' },
      { level: 'Intermediate', time: '24–29 min', value: 'intermediate' },
      { level: 'Advanced', time: '20–23 min', value: 'advanced' },
      { level: 'Elite', time: '< 20 min', value: 'elite' },
    ],
    '10k': [
      { level: 'Beginner', time: '60–70 min', value: 'beginner' },
      { level: 'Intermediate', time: '48–59 min', value: 'intermediate' },
      { level: 'Advanced', time: '42–47 min', value: 'advanced' },
      { level: 'Elite', time: '< 42 min', value: 'elite' },
    ],
    half: [
      { level: 'Beginner', time: '2h15m–2h30m', value: 'beginner' },
      { level: 'Intermediate', time: '1h45m–2h10m', value: 'intermediate' },
      { level: 'Advanced', time: '1h35m–1h44m', value: 'advanced' },
      { level: 'Elite', time: '< 1h35m', value: 'elite' },
    ],
    full: [
      { level: 'Beginner', time: '4h45m–5h30m', value: 'beginner' },
      { level: 'Intermediate', time: '3h45m–4h30m', value: 'intermediate' },
      { level: 'Advanced', time: '3h15m–3h29m', value: 'advanced' },
      { level: 'Elite', time: '< 3h15m', value: 'elite' },
    ],
  },
  workoutTypes: [
    'Easy',
    'Recovery',
    'Tempo',
    'Intervals',
    'Long',
    'Race',
    'Mixed',
    'Rest',
  ],
  defaultTrainingDays: 4,
  planPath: '/plans/json/running/',
  units: 'km',
  description: 'Build your custom running training plan',
};

// Swimming Configuration
export const swimmingConfig: SportConfig = {
  name: 'Swimming',
  sport: 'swimming',
  distances: [
    { id: '50m', label: '50m', desc: 'Sprint', raceType: '50m' },
    { id: '100m', label: '100m', desc: 'Sprint', raceType: '100m' },
    { id: '400m', label: '400m', desc: 'Middle Distance', raceType: '400m' },
    { id: '1500m', label: '1500m', desc: 'Distance', raceType: '1500m' },
    { id: '5km', label: '5km', desc: 'Open Water', raceType: '5km Swim' },
    { id: '10km', label: '10km', desc: 'Open Water', raceType: '10km Swim' },
  ],
  goalTimes: {
    '50m': [
      { level: 'Beginner', time: '> 50s', value: 'beginner' },
      { level: 'Intermediate', time: '35–50s', value: 'intermediate' },
      { level: 'Advanced', time: '28–34s', value: 'advanced' },
      { level: 'Elite', time: '< 28s', value: 'elite' },
    ],
    '100m': [
      { level: 'Beginner', time: '> 2:00', value: 'beginner' },
      { level: 'Intermediate', time: '1:30–2:00', value: 'intermediate' },
      { level: 'Advanced', time: '1:10–1:29', value: 'advanced' },
      { level: 'Elite', time: '< 1:10', value: 'elite' },
    ],
    '400m': [
      { level: 'Beginner', time: '> 9:00', value: 'beginner' },
      { level: 'Intermediate', time: '6:30–9:00', value: 'intermediate' },
      { level: 'Advanced', time: '5:30–6:29', value: 'advanced' },
      { level: 'Elite', time: '< 5:30', value: 'elite' },
    ],
    '1500m': [
      { level: 'Beginner', time: '> 35:00', value: 'beginner' },
      { level: 'Intermediate', time: '25:00–35:00', value: 'intermediate' },
      { level: 'Advanced', time: '20:00–24:59', value: 'advanced' },
      { level: 'Elite', time: '< 20:00', value: 'elite' },
    ],
    '5km': [
      { level: 'Beginner', time: '> 2h', value: 'beginner' },
      { level: 'Intermediate', time: '1h30m–2h', value: 'intermediate' },
      { level: 'Advanced', time: '1h15m–1h29m', value: 'advanced' },
      { level: 'Elite', time: '< 1h15m', value: 'elite' },
    ],
    '10km': [
      { level: 'Beginner', time: '> 4h', value: 'beginner' },
      { level: 'Intermediate', time: '3h–4h', value: 'intermediate' },
      { level: 'Advanced', time: '2h30m–2h59m', value: 'advanced' },
      { level: 'Elite', time: '< 2h30m', value: 'elite' },
    ],
  },
  workoutTypes: [
    'Easy Swim',
    'Technique',
    'Swim Intervals',
    'Long Swim',
    'Tempo Swim',
    'Rest',
  ],
  defaultTrainingDays: 4,
  planPath: '/plans/json/swimming/',
  units: 'km',
  description: 'Build your custom swimming training plan',
};

// Cycling Configuration
export const cyclingConfig: SportConfig = {
  name: 'Cycling',
  sport: 'cycling',
  distances: [
    { id: '20km', label: '20km', desc: '12.4 miles', raceType: '20km Ride' },
    { id: '40km', label: '40km', desc: '24.9 miles', raceType: '40km Ride' },
    { id: '80km', label: '80km', desc: '49.7 miles', raceType: '80km Ride' },
    { id: '100km', label: '100km', desc: '62.1 miles', raceType: '100km Ride' },
    {
      id: '160km',
      label: '160km',
      desc: '99.4 miles (Century)',
      raceType: '160km Ride',
    },
  ],
  goalTimes: {
    '20km': [
      { level: 'Beginner', time: '> 45 min', value: 'beginner' },
      { level: 'Intermediate', time: '32–45 min', value: 'intermediate' },
      { level: 'Advanced', time: '27–31 min', value: 'advanced' },
      { level: 'Elite', time: '< 27 min', value: 'elite' },
    ],
    '40km': [
      { level: 'Beginner', time: '> 1h45m', value: 'beginner' },
      { level: 'Intermediate', time: '1h15m–1h45m', value: 'intermediate' },
      { level: 'Advanced', time: '1h–1h14m', value: 'advanced' },
      { level: 'Elite', time: '< 1h', value: 'elite' },
    ],
    '80km': [
      { level: 'Beginner', time: '> 3h30m', value: 'beginner' },
      { level: 'Intermediate', time: '2h40m–3h30m', value: 'intermediate' },
      { level: 'Advanced', time: '2h10m–2h39m', value: 'advanced' },
      { level: 'Elite', time: '< 2h10m', value: 'elite' },
    ],
    '100km': [
      { level: 'Beginner', time: '> 4h30m', value: 'beginner' },
      { level: 'Intermediate', time: '3h20m–4h30m', value: 'intermediate' },
      { level: 'Advanced', time: '2h45m–3h19m', value: 'advanced' },
      { level: 'Elite', time: '< 2h45m', value: 'elite' },
    ],
    '160km': [
      { level: 'Beginner', time: '> 7h', value: 'beginner' },
      { level: 'Intermediate', time: '5h20m–7h', value: 'intermediate' },
      { level: 'Advanced', time: '4h30m–5h19m', value: 'advanced' },
      { level: 'Elite', time: '< 4h30m', value: 'elite' },
    ],
  },
  workoutTypes: [
    'Easy Ride',
    'Recovery Spin',
    'Tempo Ride',
    'Intervals Ride',
    'Long Ride',
    'Hill Repeats',
    'Rest',
  ],
  defaultTrainingDays: 4,
  planPath: '/plans/json/cycling/',
  units: 'km',
  description: 'Build your custom cycling training plan',
};

// Hyrox Configuration
export const hyroxConfig: SportConfig = {
  name: 'Hyrox',
  sport: 'hyrox',
  distances: [
    {
      id: 'women-open',
      label: 'Women Open',
      desc: '8km run + 8 stations',
      raceType: 'Hyrox Women Open',
    },
    {
      id: 'women-pro',
      label: 'Women Pro',
      desc: '8km run + 8 stations (Pro)',
      raceType: 'Hyrox Women Pro',
    },
    {
      id: 'men-open',
      label: 'Men Open',
      desc: '8km run + 8 stations',
      raceType: 'Hyrox Men Open',
    },
    {
      id: 'men-pro',
      label: 'Men Pro',
      desc: '8km run + 8 stations (Pro)',
      raceType: 'Hyrox Men Pro',
    },
  ],
  goalTimes: {
    'women-open': [
      { level: 'Beginner', time: '> 2h', value: 'beginner' },
      { level: 'Intermediate', time: '1h30m–2h', value: 'intermediate' },
      { level: 'Advanced', time: '1h15m–1h29m', value: 'advanced' },
      { level: 'Elite', time: '< 1h15m', value: 'elite' },
    ],
    'women-pro': [
      { level: 'Beginner', time: '> 1h45m', value: 'beginner' },
      { level: 'Intermediate', time: '1h20m–1h45m', value: 'intermediate' },
      { level: 'Advanced', time: '1h10m–1h19m', value: 'advanced' },
      { level: 'Elite', time: '< 1h10m', value: 'elite' },
    ],
    'men-open': [
      { level: 'Beginner', time: '> 1h45m', value: 'beginner' },
      { level: 'Intermediate', time: '1h20m–1h45m', value: 'intermediate' },
      { level: 'Advanced', time: '1h10m–1h19m', value: 'advanced' },
      { level: 'Elite', time: '< 1h10m', value: 'elite' },
    ],
    'men-pro': [
      { level: 'Beginner', time: '> 1h30m', value: 'beginner' },
      { level: 'Intermediate', time: '1h10m–1h30m', value: 'intermediate' },
      { level: 'Advanced', time: '1h–1h09m', value: 'advanced' },
      { level: 'Elite', time: '< 1h', value: 'elite' },
    ],
  },
  workoutTypes: [
    'Station Work',
    'Running Intervals',
    'Full Simulation',
    'Strength Endurance',
    'Hyrox Recovery',
    'Rest',
  ],
  defaultTrainingDays: 5,
  planPath: '/plans/json/hyrox/',
  units: 'km',
  description: 'Build your custom Hyrox training plan',
};

// Triathlon Configuration
export const triathlonConfig: SportConfig = {
  name: 'Triathlon',
  sport: 'triathlon',
  distances: [
    {
      id: 'sprint',
      label: 'Sprint',
      desc: '750m / 20km / 5km',
      raceType: 'Sprint Triathlon',
    },
    {
      id: 'standard',
      label: 'Standard',
      desc: '1.5km / 40km / 10km',
      raceType: 'Standard Triathlon',
    },
    {
      id: 'half-ironman',
      label: 'Half Ironman',
      desc: '1.9km / 90km / 21.1km',
      raceType: 'Half Ironman',
    },
    {
      id: 'ironman',
      label: 'Ironman',
      desc: '3.8km / 180km / 42.2km',
      raceType: 'Ironman',
    },
  ],
  goalTimes: {
    sprint: [
      { level: 'Beginner', time: '> 1h45m', value: 'beginner' },
      { level: 'Intermediate', time: '1h20m–1h45m', value: 'intermediate' },
      { level: 'Advanced', time: '1h05m–1h19m', value: 'advanced' },
      { level: 'Elite', time: '< 1h05m', value: 'elite' },
    ],
    standard: [
      { level: 'Beginner', time: '> 3h30m', value: 'beginner' },
      { level: 'Intermediate', time: '2h45m–3h30m', value: 'intermediate' },
      { level: 'Advanced', time: '2h20m–2h44m', value: 'advanced' },
      { level: 'Elite', time: '< 2h20m', value: 'elite' },
    ],
    'half-ironman': [
      { level: 'Beginner', time: '> 7h', value: 'beginner' },
      { level: 'Intermediate', time: '5h30m–7h', value: 'intermediate' },
      { level: 'Advanced', time: '4h45m–5h29m', value: 'advanced' },
      { level: 'Elite', time: '< 4h45m', value: 'elite' },
    ],
    ironman: [
      { level: 'Beginner', time: '> 15h', value: 'beginner' },
      { level: 'Intermediate', time: '12h–15h', value: 'intermediate' },
      { level: 'Advanced', time: '10h–11h59m', value: 'advanced' },
      { level: 'Elite', time: '< 10h', value: 'elite' },
    ],
  },
  workoutTypes: [
    'Swim',
    'Bike',
    'Run',
    'Brick',
    'Transition Practice',
    'Long Tri',
    'Tri Recovery',
    'Rest',
  ],
  defaultTrainingDays: 6,
  planPath: '/plans/json/triathlon/',
  units: 'km',
  description: 'Build your custom triathlon training plan',
};

// Sport lookup map
export const sportConfigs: Record<Sport, SportConfig> = {
  running: runningConfig,
  swimming: swimmingConfig,
  cycling: cyclingConfig,
  hyrox: hyroxConfig,
  triathlon: triathlonConfig,
};

export function getSportConfig(sport: Sport): SportConfig {
  return sportConfigs[sport];
}
